import React, { useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import {
  Users,
  FileText,
  Megaphone,
  Clock,
  Globe,
  Server,
  Share2,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  Sparkles,
  X,
  TrendingUp,
  Mail,
  BarChart3,
  Activity,
} from 'lucide-react';
import { GlassCard, GlassBadge } from '../ui';
import { ModuleHeader } from '../ui/ModuleHeader';
import {
  getDashboardSummary,
  generateDashboardHeroNarrative,
  DashboardSummary,
} from '../../services/api';
import PredictionDashboardWidget from '../Predictions/PredictionDashboardWidget';
import OptimizationDashboardWidget from '../Optimization/OptimizationDashboardWidget';
import AIReferrerWidget from './widgets/AIReferrerWidget';
import ContentQuickstartCard from './ContentQuickstartCard';
import './Dashboard.css';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};

// ─────────────────────────────────────────────────────────────────
// KPI Card
// ─────────────────────────────────────────────────────────────────
interface KPICardProps {
  icon: React.ComponentType<{ size?: number; color?: string }>;
  label: string;
  value: number;
  delta?: number;
  suffix?: string;
  variant?: 'default' | 'warning';
  link?: string;
}

const KPICardWidget: React.FC<KPICardProps> = ({
  icon: Icon,
  label,
  value,
  delta,
  suffix,
  variant = 'default',
  link,
}) => {
  const navigate = useNavigate();
  const borderStyle: React.CSSProperties =
    variant === 'warning' && value > 0
      ? { border: '1px solid var(--warning)', borderRadius: 'var(--radius-lg)' }
      : {};

  return (
    <GlassCard
      className="dash-kpi-card"
      style={borderStyle}
      onClick={link ? () => navigate(link) : undefined}
    >
      <div className="dash-kpi-icon-wrap">
        <Icon size={20} />
      </div>
      <div className="dash-kpi-value">{value.toLocaleString()}</div>
      <div className="dash-kpi-label">
        {label}
        {suffix && <span style={{ fontStyle: 'italic', marginLeft: 4 }}>{suffix}</span>}
      </div>
      {delta !== undefined && delta !== 0 && (
        <div className={`dash-kpi-delta ${delta > 0 ? 'up' : 'down'}`}>
          {delta > 0 ? '▲' : '▼'} {Math.abs(delta)}% vs last week
        </div>
      )}
    </GlassCard>
  );
};

// ─────────────────────────────────────────────────────────────────
// Hero Card
// ─────────────────────────────────────────────────────────────────
interface HeroCardProps {
  narrative: string | null;
  loading: boolean;
  onRefresh: () => void;
}

const HeroCard: React.FC<HeroCardProps> = ({ narrative, loading, onRefresh }) => (
  <GlassCard className="dash-hero-card">
    <div className="dash-hero-header">
      <Sparkles size={18} color="#2177ff" />
      <span className="dash-hero-title">What AiMe Did For You</span>
      <span
        style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginRight: 8 }}
      >
        This week at a glance
      </span>
      <button
        className="dash-refresh-btn"
        onClick={onRefresh}
        disabled={loading}
        title="Refresh narrative"
      >
        <RefreshCw size={14} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
        {loading ? 'Generating…' : 'Refresh'}
      </button>
    </div>
    <div className="dash-hero-body">
      {loading ? (
        <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>
          Generating your weekly insight…
        </span>
      ) : narrative ? (
        narrative
      ) : (
        <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>
          Click Refresh to generate your first weekly insight.
        </span>
      )}
    </div>
  </GlassCard>
);

// ─────────────────────────────────────────────────────────────────
// Setup Progress Card
// ─────────────────────────────────────────────────────────────────
interface SetupProgressCardProps {
  setup: DashboardSummary['setup_progress'];
}

const SetupProgressCard: React.FC<SetupProgressCardProps & { onDismiss: () => void }> = ({ setup, onDismiss }) => {
  const navigate = useNavigate();
  const items: { key: keyof typeof setup; label: string; link: string }[] = [
    { key: 'brand_guide',    label: 'Create Brand Guide',     link: '/brand' },
    { key: 'website',        label: 'Build Your Website',     link: '/site-builder' },
    { key: 'content',        label: 'Create Content',         link: '/content' },
    { key: 'landing_page',   label: 'Create Landing Page',    link: '/landing-pages' },
    { key: 'campaign',       label: 'Launch a Campaign',      link: '/campaigns' },
    { key: 'social_account', label: 'Connect Social Account', link: '/social-accounts' },
    { key: 'email_sequence', label: 'Set Up Email Sequence',  link: '/campaigns' },
  ];

  const completed = items.filter(i => setup[i.key]).length;

  return (
    <GlassCard className="dash-setup-card">
      <div className="dash-widget-header">
        <Activity size={16} color="#2177ff" />
        <span className="dash-widget-title">Get Started with AiMe</span>
        <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
          {completed}/{items.length} complete
        </span>
        <button
          onClick={onDismiss}
          title="Dismiss"
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--text-secondary)', display: 'flex', alignItems: 'center',
            padding: '2px', borderRadius: 4, marginLeft: 8, transition: 'color 0.15s',
          }}
          onMouseEnter={e => (e.currentTarget.style.color = 'var(--text)')}
          onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-secondary)')}
        >
          <X size={14} />
        </button>
      </div>
      {items.map(({ key, label, link }) => (
        <div className="dash-setup-item" key={key}>
          {setup[key] ? (
            <CheckCircle size={16} className="dash-setup-check" />
          ) : (
            <div
              style={{
                width: 16,
                height: 16,
                borderRadius: '50%',
                border: '2px solid var(--stroke)',
                flexShrink: 0,
              }}
            />
          )}
          <span style={{ flex: 1 }}>{label}</span>
          {!setup[key] && (
            <span
              className="dash-setup-link"
              onClick={() => navigate(link)}
              role="button"
              tabIndex={0}
              onKeyDown={e => e.key === 'Enter' && navigate(link)}
            >
              Set up →
            </span>
          )}
        </div>
      ))}
    </GlassCard>
  );
};

// ─────────────────────────────────────────────────────────────────
// Action Alerts Card
// ─────────────────────────────────────────────────────────────────
interface ActionAlertsCardProps {
  alerts: DashboardSummary['action_alerts'];
}

const ActionAlertsCard: React.FC<ActionAlertsCardProps> = ({ alerts }) => {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const visible = alerts.filter(a => !dismissed.has(a.id));
  if (visible.length === 0) return null;

  const alertIcon = (type: string) => {
    switch (type) {
      case 'danger': return <AlertTriangle size={16} color="var(--danger)" />;
      case 'success': return <CheckCircle size={16} color="var(--success)" />;
      case 'info': return <Activity size={16} color="var(--info)" />;
      default: return <AlertTriangle size={16} color="var(--warning)" />;
    }
  };

  return (
    <GlassCard className="dash-alerts-card">
      <div className="dash-widget-header">
        <AlertTriangle size={16} color="var(--warning)" />
        <span className="dash-widget-title">Action Required</span>
        <GlassBadge variant="warning" size="sm">{visible.length}</GlassBadge>
      </div>
      {visible.map(alert => (
        <div className="dash-alert-row" key={alert.id}>
          {alertIcon(alert.type)}
          <div className="dash-alert-body">
            <div className="dash-alert-title">{alert.title}</div>
            {alert.body && <div className="dash-alert-text">{alert.body}</div>}
          </div>
          {alert.link && (
            <span
              className="dash-setup-link"
              onClick={() => navigate(alert.link!)}
              role="button"
              tabIndex={0}
              onKeyDown={e => e.key === 'Enter' && navigate(alert.link!)}
            >
              View →
            </span>
          )}
          <button
            onClick={() => setDismissed(prev => new Set(Array.from(prev).concat(alert.id)))}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 4,
              color: 'var(--text-secondary)',
              display: 'flex',
              alignItems: 'center',
            }}
            title="Dismiss"
          >
            <X size={14} />
          </button>
        </div>
      ))}
    </GlassCard>
  );
};

// ─────────────────────────────────────────────────────────────────
// Lead Trend Card
// ─────────────────────────────────────────────────────────────────
interface LeadTrendCardProps {
  trend: DashboardSummary['lead_trend'];
}

const LeadTrendCard: React.FC<LeadTrendCardProps> = ({ trend }) => {
  const maxCount = Math.max(...trend.map(t => t.count), 1);
  const total = trend.reduce((s, t) => s + t.count, 0);

  return (
    <GlassCard className="dash-lead-card">
      <div className="dash-widget-header">
        <TrendingUp size={16} color="#2177ff" />
        <span className="dash-widget-title">Lead Trend</span>
        <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
          {total} total (4 weeks)
        </span>
      </div>
      {total === 0 ? (
        <div className="dash-empty-state">
          No lead data yet.{' '}
          <span
            className="dash-empty-link"
            onClick={() => {}}
            role="button"
            tabIndex={0}
          >
            Set up lead capture →
          </span>
        </div>
      ) : (
        <div className="dash-bars">
          {trend.map(t => (
            <div className="dash-bar-wrap" key={t.week_label}>
              <div className="dash-bar-count">{t.count}</div>
              <div
                className="dash-bar"
                style={{ height: `${Math.max((t.count / maxCount) * 100, 4)}%` }}
              />
              <div className="dash-bar-label">{t.week_label}</div>
            </div>
          ))}
        </div>
      )}
    </GlassCard>
  );
};

// ─────────────────────────────────────────────────────────────────
// Top Campaign Card
// ─────────────────────────────────────────────────────────────────
interface TopCampaignCardProps {
  campaign: DashboardSummary['top_campaign'];
}

const TopCampaignCard: React.FC<TopCampaignCardProps> = ({ campaign }) => {
  const navigate = useNavigate();

  const goalBadgeVariant = (goal: string): 'success' | 'warning' | 'info' | 'default' => {
    switch (goal) {
      case 'lead_gen': return 'success';
      case 'promotion': return 'warning';
      case 'awareness': return 'info';
      default: return 'default';
    }
  };

  const statusBadgeVariant = (status: string): 'success' | 'warning' | 'danger' | 'default' => {
    switch (status) {
      case 'active': return 'success';
      case 'paused': return 'warning';
      case 'draft': return 'default';
      case 'completed': return 'info' as any;
      default: return 'default';
    }
  };

  return (
    <GlassCard>
      <div className="dash-widget-header">
        <Megaphone size={16} color="#2177ff" />
        <span className="dash-widget-title">Top Campaign</span>
      </div>
      {!campaign ? (
        <div className="dash-empty-state">
          No active campaigns.{' '}
          <span
            className="dash-empty-link"
            onClick={() => navigate('/campaigns')}
            role="button"
            tabIndex={0}
            onKeyDown={e => e.key === 'Enter' && navigate('/campaigns')}
          >
            Create Campaign →
          </span>
        </div>
      ) : (
        <div style={{ padding: '8px 0' }}>
          <div
            style={{
              fontWeight: 600,
              fontSize: '0.95rem',
              color: 'var(--text)',
              marginBottom: 10,
            }}
          >
            {campaign.name}
          </div>
          <div
            style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}
          >
            {campaign.goal && (
              <GlassBadge variant={goalBadgeVariant(campaign.goal)} size="sm">
                {campaign.goal.replace('_', ' ')}
              </GlassBadge>
            )}
            <GlassBadge variant={statusBadgeVariant(campaign.status)} size="sm">
              {campaign.status}
            </GlassBadge>
            <span
              style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginLeft: 'auto' }}
            >
              {campaign.asset_count} asset{campaign.asset_count !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      )}
    </GlassCard>
  );
};

// ─────────────────────────────────────────────────────────────────
// Upcoming Content Card
// ─────────────────────────────────────────────────────────────────
interface UpcomingContentCardProps {
  items: DashboardSummary['upcoming_content'];
}

const UpcomingContentCard: React.FC<UpcomingContentCardProps> = ({ items }) => {
  const navigate = useNavigate();

  const formatDate = (iso: string) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-AU', { month: 'short', day: 'numeric' });
  };

  const truncate = (str: string, n: number) =>
    str.length > n ? str.slice(0, n) + '…' : str;

  return (
    <GlassCard>
      <div className="dash-widget-header">
        <FileText size={16} color="#2177ff" />
        <span className="dash-widget-title">Upcoming Content</span>
      </div>
      {items.length === 0 ? (
        <div className="dash-empty-state">
          No scheduled content.{' '}
          <span
            className="dash-empty-link"
            onClick={() => navigate('/scheduling')}
            role="button"
            tabIndex={0}
            onKeyDown={e => e.key === 'Enter' && navigate('/scheduling')}
          >
            Go to Scheduling →
          </span>
        </div>
      ) : (
        items.map((item, idx) => (
          <div className="dash-upcoming-item" key={idx}>
            <span className="dash-upcoming-title">{truncate(item.title, 40)}</span>
            {item.platform && (
              <GlassBadge variant="info" size="sm">{item.platform}</GlassBadge>
            )}
            <span className="dash-upcoming-date">{formatDate(item.scheduled_at)}</span>
          </div>
        ))
      )}
    </GlassCard>
  );
};

// ─────────────────────────────────────────────────────────────────
// Platform Status Card
// ─────────────────────────────────────────────────────────────────
interface PlatformStatusCardProps {
  status: DashboardSummary['platform_status'];
  abTests: number;
  autonomyMode: string;
  socialAccounts: DashboardSummary['social_follower_growth'];
}

const PlatformStatusCard: React.FC<PlatformStatusCardProps> = ({
  status,
  abTests,
  autonomyMode,
  socialAccounts,
}) => {
  const autonomyBadge = (mode: string): 'success' | 'warning' | 'default' => {
    if (mode === 'auto') return 'success';
    if (mode === 'review') return 'warning';
    return 'default';
  };

  return (
    <GlassCard>
      <div className="dash-widget-header">
        <BarChart3 size={16} color="#2177ff" />
        <span className="dash-widget-title">Platform Status</span>
      </div>

      <div className="dash-platform-row">
        <Globe size={15} color="var(--text-secondary)" />
        <span style={{ flex: 1 }}>Domains</span>
        <span className="dash-platform-count">{status.domains_active}</span>
      </div>
      <div className="dash-platform-row">
        <Server size={15} color="var(--text-secondary)" />
        <span style={{ flex: 1 }}>WordPress Sites</span>
        <span className="dash-platform-count">{status.wordpress_sites}</span>
      </div>
      <div className="dash-platform-row">
        <Share2 size={15} color="var(--text-secondary)" />
        <span style={{ flex: 1 }}>Social Accounts</span>
        <span className="dash-platform-count">{status.social_accounts}</span>
      </div>
      <div className="dash-platform-row">
        <Mail size={15} color="var(--text-secondary)" />
        <span style={{ flex: 1 }}>Active A/B Tests</span>
        <span className="dash-platform-count">{abTests}</span>
      </div>
      <div className="dash-platform-row" style={{ borderBottom: 'none' }}>
        <Activity size={15} color="var(--text-secondary)" />
        <span style={{ flex: 1 }}>AiMe Agent</span>
        <GlassBadge variant={autonomyBadge(autonomyMode)} size="sm">
          {autonomyMode}
        </GlassBadge>
      </div>
    </GlassCard>
  );
};

// ─────────────────────────────────────────────────────────────────
// AI Insight Card
// ─────────────────────────────────────────────────────────────────
interface AIInsightCardProps {
  narrative: string | null;
  loading: boolean;
}

const AIInsightCard: React.FC<AIInsightCardProps> = ({ narrative, loading }) => (
  <GlassCard
    className="dash-insight-card"
    style={{
      background:
        'linear-gradient(135deg, rgba(33,119,255,0.06), rgba(30,172,166,0.06))',
    }}
  >
    <div className="dash-insight-header">
      <Sparkles size={18} color="#2177ff" />
      <span className="dash-insight-title">AI Weekly Insight</span>
    </div>
    {loading ? (
      <div className="dash-skeleton-box" style={{ height: 60, borderRadius: 8 }} />
    ) : narrative ? (
      <div className="dash-insight-body">{narrative}</div>
    ) : (
      <div className="dash-insight-body" style={{ fontStyle: 'normal', color: 'var(--text-secondary)' }}>
        Click the refresh button above to generate your first insight.
      </div>
    )}
  </GlassCard>
);

// ─────────────────────────────────────────────────────────────────
// Loading Skeleton
// ─────────────────────────────────────────────────────────────────
const LoadingSkeleton: React.FC = () => (
  <div>
    <div className="dash-skeleton-row">
      {[1, 2, 3, 4].map(i => (
        <div key={i} className="dash-skeleton-box" style={{ flex: 1, height: 120 }} />
      ))}
    </div>
    <div className="dash-skeleton-row">
      <div className="dash-skeleton-box" style={{ flex: 1, height: 200 }} />
      <div className="dash-skeleton-box" style={{ flex: 1, height: 200 }} />
    </div>
    <div className="dash-skeleton-row">
      <div className="dash-skeleton-box" style={{ flex: 1, height: 200 }} />
      <div className="dash-skeleton-box" style={{ flex: 1, height: 200 }} />
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────
// Error Card
// ─────────────────────────────────────────────────────────────────
interface ErrorCardProps {
  message: string;
  onRetry: () => void;
}

const ErrorCard: React.FC<ErrorCardProps> = ({ message, onRetry }) => (
  <GlassCard style={{ padding: 24, textAlign: 'center', marginBottom: 20 }}>
    <AlertTriangle size={32} color="var(--danger)" style={{ marginBottom: 12 }} />
    <div style={{ color: 'var(--text)', marginBottom: 8 }}>Failed to load dashboard</div>
    <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: 16 }}>
      {message}
    </div>
    <button
      onClick={onRetry}
      style={{
        background: '#2177ff',
        color: '#fff',
        border: 'none',
        borderRadius: 9999,
        padding: '8px 20px',
        cursor: 'pointer',
        fontSize: '0.88rem',
      }}
    >
      Retry
    </button>
  </GlassCard>
);

// ─────────────────────────────────────────────────────────────────
// Main Dashboard Component
// ─────────────────────────────────────────────────────────────────
export const Dashboard: React.FC = () => {
  const ctx = useOutletContext<{ selectedProjectId?: string }>();
  const selectedProjectId = ctx?.selectedProjectId;

  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [narrativeLoading, setNarrativeLoading] = useState(false);
  const [narrative, setNarrative] = useState<string | null>(null);

  const loadSummary = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getDashboardSummary(selectedProjectId);
      setSummary(data);
      if (data.hero_narrative) {
        setNarrative(data.hero_narrative);
      }
    } catch (err: any) {
      setError(getApiError(err, err?.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshNarrative = async () => {
    setNarrativeLoading(true);
    try {
      const result = await generateDashboardHeroNarrative();
      setNarrative(result.narrative);
    } catch (err) {
      // Silently fail — narrative is optional
    } finally {
      setNarrativeLoading(false);
    }
  };

  useEffect(() => {
    loadSummary();
  }, [selectedProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const isSetupComplete = summary
    ? Object.values(summary.setup_progress).every(Boolean)
    : false;

  const [setupDismissed, setSetupDismissed] = useState(
    () => localStorage.getItem('aime_setup_card_dismissed') === 'true'
  );

  const handleDismissSetup = () => {
    localStorage.setItem('aime_setup_card_dismissed', 'true');
    setSetupDismissed(true);
  };

  return (
    <div className="dashboard-container">
      <ModuleHeader title="Dashboard" breadcrumb={['Home', 'Dashboard']} />

      {loading ? (
        <LoadingSkeleton />
      ) : error ? (
        <ErrorCard message={error} onRetry={loadSummary} />
      ) : summary ? (
        <>
          {/* Hero Narrative */}
          <HeroCard
            narrative={narrative}
            loading={narrativeLoading}
            onRefresh={handleRefreshNarrative}
          />

          {/* Content Quickstart Card — state-aware masterplan/plan/scheduling guide */}
          {selectedProjectId && (
            <ContentQuickstartCard projectId={selectedProjectId} />
          )}

          {/* Setup Progress — hidden once fully complete or manually dismissed */}
          {!isSetupComplete && !setupDismissed && (
            <SetupProgressCard setup={summary.setup_progress} onDismiss={handleDismissSetup} />
          )}

          {/* KPI Row */}
          <div className="dash-kpi-row">
            <KPICardWidget
              icon={Users}
              label="New Leads"
              value={summary.kpis.new_leads_this_week}
              delta={summary.kpis.new_leads_delta_pct}
              suffix="this week"
              link="/crm"
            />
            <KPICardWidget
              icon={FileText}
              label="Posts Published"
              value={summary.kpis.posts_published_this_week}
              suffix="this week"
              link="/scheduling"
            />
            <KPICardWidget
              icon={Megaphone}
              label="Active Campaigns"
              value={summary.kpis.active_campaigns}
              link="/campaigns"
            />
            <KPICardWidget
              icon={Clock}
              label="Pending Approvals"
              value={summary.kpis.pending_approvals}
              variant={summary.kpis.pending_approvals > 0 ? 'warning' : 'default'}
              link="/agent"
            />
          </div>

          {/* Action Required Alerts */}
          {summary.action_alerts.length > 0 && (
            <ActionAlertsCard alerts={summary.action_alerts} />
          )}

          {/* Growth Signals Row */}
          <div className="dash-two-col">
            <LeadTrendCard trend={summary.lead_trend} />
            <TopCampaignCard campaign={summary.top_campaign} />
          </div>

          {/* Platform & Content Health Row */}
          <div className="dash-two-col">
            <UpcomingContentCard items={summary.upcoming_content} />
            <PlatformStatusCard
              status={summary.platform_status}
              abTests={summary.ab_tests_active}
              autonomyMode={summary.agent_autonomy_mode}
              socialAccounts={summary.social_follower_growth}
            />
          </div>

          {/* AI Weekly Insight */}
          <AIInsightCard narrative={narrative} loading={narrativeLoading} />

          {/* Predictive Analytics */}
          {selectedProjectId && (
            <PredictionDashboardWidget projectId={selectedProjectId} compact />
          )}

          {/* Adaptive Optimization */}
          {selectedProjectId && (
            <OptimizationDashboardWidget projectId={selectedProjectId} />
          )}

          {/* AI Referrals — pages AI assistants cite (returns null when no traffic) */}
          {selectedProjectId && (
            <AIReferrerWidget projectId={selectedProjectId} />
          )}
        </>
      ) : null}
    </div>
  );
};

export default Dashboard;
